'use client'

import { Icons } from '@/components/ui/icons'

export interface AppNavItem {
  href: string
  label: string
  icon: React.ElementType
  adminOnly?: boolean
  keywords?: string[]
  /** Set to true to hide from the UI without removing from the codebase. Remove to re-show. */
  hidden?: boolean
}

export interface AppNavSection {
  title: string
  items: AppNavItem[]
}

export const APP_NAV_SECTIONS: AppNavSection[] = [
  {
    title: 'Platform',
    items: [
      {
        href: '/',
        label: 'Dashboard',
        icon: Icons.dashboard,
        keywords: ['home', 'overview', 'metrics'],
      },
    ],
  },
  {
    title: 'AP Control Tower',
    items: [
      {
        href: '/ap-center',
        label: 'Approval Workbench',
        icon: Icons.checkCircle,
        keywords: ['ap', 'invoices', 'approval', 'workbench', 'inbox'],
      },
      {
        href: '/ap-center/historical',
        label: 'Historical Data',
        icon: Icons.archive,
        adminOnly: true,
        keywords: ['historical', 'history', 'archive', 'past', 'invoices'],
      },
    ],
  },
  {
    title: 'AI Intelligence',
    items: [
      {
        href: '/ai/policies',
        label: 'AI Policies',
        icon: Icons.brain,
        adminOnly: true,
        keywords: ['policies', 'rules', 'automation'],
      },
      {
        href: '/ai/insights',
        label: 'AI Insights',
        icon: Icons.lightbulb,
        adminOnly: true,
        keywords: ['insights', 'analysis', 'recommendations'],
        hidden: true,
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        href: '/admin/users',
        label: 'User Management',
        icon: Icons.users,
        adminOnly: true,
        keywords: ['users', 'accounts', 'access'],
      },
      {
        href: '/admin/roles',
        label: 'Role Management',
        icon: Icons.shield,
        adminOnly: true,
        keywords: ['roles', 'permissions'],
      },
      {
        href: '/admin/groups',
        label: 'Group Management',
        icon: Icons.network,
        adminOnly: true,
        keywords: ['groups', 'teams'],
        hidden: true, // HIDDEN: remove this line to re-show Group Management in the sidebar
      },
      {
        href: '/admin/sessions',
        label: 'User Sessions',
        icon: Icons.device,
        adminOnly: true,
        keywords: ['sessions', 'logins', 'devices'],
      },
      {
        href: '/admin/events',
        label: 'Login Events',
        icon: Icons.activity,
        adminOnly: true,
        keywords: ['events', 'logins', 'activity'],
      },
      {
        href: '/admin/audit',
        label: 'Audit Logs',
        icon: Icons.fileText,
        adminOnly: true,
        keywords: ['audit', 'logs', 'history'],
      },
      {
        href: '/admin/settings',
        label: 'Admin Settings',
        icon: Icons.settings,
        adminOnly: true,
        keywords: ['admin', 'settings', 'configuration'],
      },
    ],
  },
  {
    title: 'System',
    items: [
      // HIDDEN: Settings nav item — to re-show, uncomment below
      // {
      //   href: '/settings',
      //   label: 'Settings',
      //   icon: Icons.settings,
      //   keywords: ['preferences', 'settings', 'profile'],
      // },
    ],
  },
]

export function getVisibleNavSections(isAdmin: boolean): AppNavSection[] {
  return APP_NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (!item.adminOnly || isAdmin) && !item.hidden),
    }))
    .filter((section) => section.items.length > 0)
}

export function getVisibleNavItems(isAdmin: boolean): AppNavItem[] {
  return getVisibleNavSections(isAdmin).flatMap((section) => section.items)
}
